import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Boxes,
  CheckCircle2,
  Handshake,
  Package,
  Printer,
  ReceiptText,
  TrendingUp,
} from "lucide-react";
import { api, apiError, brl, fileUrl, gramas, kg } from "@/lib/api";
import { ErrorState, Loading, PageHeader, Surface, Thumb } from "@/components/ds";
import { DashboardFinance } from "@/components/DashboardFinance";

const dataCurta = (iso) =>
  iso ? new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) : "";

const hora = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "";

const StatCard = ({ label, value, sub, icon: Icon, testid, accent, to }) => {
  const body = (
    <Surface className="h-full p-5 transition-transform hover:-translate-y-1" data-testid={testid}>
      <div className="flex items-start justify-between">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <Icon className={`h-4 w-4 ${accent || "text-brand"}`} />
      </div>
      <p className="mt-3 font-num text-2xl font-medium">{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </Surface>
  );
  return to ? (
    <Link to={to} className="block">
      {body}
    </Link>
  ) : (
    body
  );
};

const SectionLink = ({ to, children, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="mt-5 inline-flex items-center gap-1 text-xs font-medium text-brand transition-colors hover:text-brand/80"
  >
    {children} <ArrowRight className="h-3 w-3" />
  </Link>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setError("");
    setData(null);
    api
      .get("/dashboard/summary")
      .then(({ data }) => setData(data))
      .catch((e) => setError(apiError(e)));
  }, []);

  useEffect(load, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading label="Carregando seu resumo..." />;

  const attention = [
    data.pending_orders > 0 && {
      key: "orders",
      text: `${data.pending_orders} ${data.pending_orders === 1 ? "pedido aguardando" : "pedidos aguardando"} pagamento`,
      to: "/pedidos",
      action: "Abrir Pedidos",
    },
    data.productions_pending > 0 && {
      key: "prod-pending",
      text: `${data.productions_pending} ${data.productions_pending === 1 ? "produção pendente" : "produções pendentes"}`,
      to: "/producao",
      action: "Abrir Produção",
    },
    data.productions_running > 0 && {
      key: "prod-running",
      text: `${data.productions_running} ${data.productions_running === 1 ? "produção em andamento" : "produções em andamento"}`,
      to: "/producao",
      action: "Abrir Produção",
    },
    data.low_stock.length > 0 && {
      key: "filaments",
      text: `${data.low_stock.length} ${data.low_stock.length === 1 ? "filamento abaixo" : "filamentos abaixo"} do mínimo`,
      to: "/estoque",
      action: "Abrir Estoque",
    },
    data.low_materials?.length > 0 && {
      key: "materials",
      text: `${data.low_materials.length} ${data.low_materials.length === 1 ? "material abaixo" : "materiais abaixo"} do mínimo`,
      to: "/estoque",
      action: "Abrir Estoque",
    },
    data.replenishments_open > 0 && {
      key: "replenishments",
      text: `${data.replenishments_open} ${data.replenishments_open === 1 ? "reposição aguardando" : "reposições aguardando"} ação`,
      to: "/parceiros",
      action: "Abrir Parceiros",
    },
    data.returns_open > 0 && {
      key: "returns",
      text: `${data.returns_open} ${data.returns_open === 1 ? "devolução aguardando" : "devoluções aguardando"} ação`,
      to: "/parceiros",
      action: "Abrir Parceiros",
    },
  ].filter(Boolean);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <PageHeader
        title="Minha empresa hoje"
        subtitle="Um resumo rápido do que você tem e do que está acontecendo."
      />

      <div className="grid grid-cols-2 gap-4 stagger lg:grid-cols-3 xl:grid-cols-6">
        <StatCard
          label="Vendido no mês"
          value={brl(data.finance?.sold ?? 0)}
          sub={`${data.finance?.orders ?? 0} pedidos no mês`}
          icon={TrendingUp}
          testid="stat-sales"
          to="/financeiro"
        />
        <StatCard
          label="Pedidos"
          value={data.orders_count}
          sub={`${data.pending_orders || 0} aguardando pagamento`}
          icon={ReceiptText}
          testid="stat-orders"
          to="/pedidos"
        />
        <StatCard
          label="Em produção"
          value={data.in_production}
          sub={`${data.productions_pending || 0} na fila · ${data.productions_running || 0} rodando`}
          icon={Printer}
          testid="stat-production"
          to="/producao"
        />
        <StatCard
          label="Estoque de filamentos"
          value={kg(data.filament_total_g)}
          sub={`${data.filaments_count} cores`}
          icon={Boxes}
          testid="stat-filament"
          accent="text-emerald-400"
          to="/estoque"
        />
        <StatCard
          label="Produtos em estoque"
          value={data.products_units ?? 0}
          sub={`${data.products_count} produtos cadastrados`}
          icon={Package}
          testid="stat-products"
          to="/estoque"
        />
        <StatCard
          label="Parceiros ativos"
          value={data.partners_active ?? 0}
          sub={`${data.replenishments_open || 0} reposições abertas`}
          icon={Handshake}
          testid="stat-partners"
          to="/parceiros"
        />
      </div>

      <DashboardFinance finance={data.finance} />

      <Surface
        className={`p-6 ${attention.length ? "border-amber-500/30 bg-amber-500/5" : ""}`}
        data-testid="attention-card"
      >
        <div className="flex items-center gap-2">
          {attention.length ? (
            <AlertTriangle className="h-4 w-4 text-amber-400" />
          ) : (
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
          )}
          <h2 className="font-display text-lg">Precisa da sua atenção</h2>
        </div>
        {attention.length === 0 ? (
          <div className="mt-4" data-testid="attention-empty">
            <p className="text-sm text-emerald-300">Está tudo em dia</p>
            <p className="text-xs text-muted-foreground">
              Nenhuma pendência importante no momento.
            </p>
          </div>
        ) : (
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {attention.map((a) => (
              <li key={a.key} data-testid={`attention-item-${a.key}`}>
                <Link
                  to={a.to}
                  className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-secondary/40 p-3 transition-colors hover:border-brand/40"
                >
                  <span className="min-w-0 text-sm">{a.text}</span>
                  <span className="shrink-0 text-[11px] text-brand">{a.action} →</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Surface>

      <div className="grid gap-6 lg:grid-cols-2">
        <Surface className="p-6" data-testid="production-now-card">
          <div className="flex items-center gap-2">
            <Printer className="h-4 w-4 text-brand" />
            <h2 className="font-display text-lg">Produção agora</h2>
            <span className="ml-auto font-num text-xs text-muted-foreground">
              {data.printers_available} de {data.printers?.length || 0} livres
            </span>
          </div>
          {(data.printers || []).length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground" data-testid="printers-empty">
              Cadastre suas impressoras para acompanhar a produção aqui.
            </p>
          ) : (
            <ul className="mt-4 space-y-2">
              {data.printers.map((pr) => (
                <li
                  key={pr.printer_id}
                  className={`rounded-lg border px-3 py-2.5 ${
                    pr.available
                      ? "border-emerald-500/25 bg-emerald-500/5"
                      : "border-amber-500/30 bg-amber-500/5"
                  }`}
                  data-testid={`dash-printer-${pr.printer_id}`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${
                        pr.available ? "bg-emerald-400" : "bg-amber-400"
                      }`}
                    />
                    <p className="min-w-0 truncate text-sm">{pr.name}</p>
                    <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
                      {pr.available ? "Livre" : pr.current_production?.name}
                    </span>
                  </div>
                  {pr.current_production?.eta && (
                    <p className="mt-1 font-num text-[10px] text-muted-foreground">
                      Previsão: {hora(pr.current_production.eta)}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 font-num text-xs text-muted-foreground">
            {data.productions_pending || 0} produções pendentes na fila
          </p>
          <SectionLink to="/producao" testid="go-to-production-link">
            Ver produção
          </SectionLink>
        </Surface>

        <Surface className="p-6" data-testid="recent-orders-card">
          <div className="flex items-center gap-2">
            <ReceiptText className="h-4 w-4 text-brand" />
            <h2 className="font-display text-lg">Pedidos recentes</h2>
          </div>
          {(data.recent_orders || []).length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground" data-testid="recent-orders-empty">
              Nenhum pedido registrado ainda.
            </p>
          ) : (
            <ul className="mt-4 space-y-2">
              {data.recent_orders.map((o) => (
                <li
                  key={o.id}
                  className="rounded-lg border border-white/10 bg-secondary/40 p-3"
                  data-testid={`recent-order-${o.id}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="min-w-0 truncate text-sm">{o.customer_name}</p>
                    <span className="shrink-0 font-num text-sm">{brl(o.total)}</span>
                  </div>
                  <p className="font-num text-[11px] text-muted-foreground">
                    {dataCurta(o.created_at)} · {o.status_label}
                  </p>
                </li>
              ))}
            </ul>
          )}
          <SectionLink to="/pedidos" testid="go-to-orders-link">
            Ver todos os pedidos
          </SectionLink>
        </Surface>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Surface className="p-6 lg:col-span-2" data-testid="low-stock-card">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            <h2 className="font-display text-lg">Estoque e alertas</h2>
          </div>
          {data.low_stock.length === 0 && (data.low_materials || []).length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground" data-testid="low-stock-empty">
              Tudo em ordem — nada abaixo do mínimo.
            </p>
          ) : (
            <ul className="mt-4 space-y-3">
              {data.low_stock.slice(0, 4).map((f) => (
                <li
                  key={f.id}
                  className="flex items-center gap-3 rounded-lg border border-white/10 bg-secondary/40 p-3"
                  data-testid={`low-stock-item-${f.id}`}
                >
                  <Thumb src={fileUrl(f.photo_path)} alt={f.label} className="h-10 w-10" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{f.label}</p>
                    <p className="text-xs text-muted-foreground">
                      Mínimo: {gramas(f.min_stock_g)}
                    </p>
                  </div>
                  <span className="font-num text-sm text-red-300">{gramas(f.balance_g)}</span>
                </li>
              ))}
              {(data.low_materials || []).slice(0, 3).map((m) => (
                <li
                  key={m.id}
                  className="flex items-center gap-3 rounded-lg border border-white/10 bg-secondary/40 p-3"
                  data-testid={`low-material-item-${m.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{m.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Mínimo: {m.min_stock} {m.unit}
                    </p>
                  </div>
                  <span className="font-num text-sm text-red-300">
                    {m.quantity} {m.unit}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <SectionLink to="/estoque" testid="go-to-stock-link">
            Ver estoque
          </SectionLink>
        </Surface>

        <Surface className="p-6" data-testid="month-card">
          <h2 className="font-display text-lg">Minha empresa este mês</h2>
          <dl className="mt-4 space-y-4 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-muted-foreground">Faturamento</dt>
              <dd className="font-num" data-testid="month-sales">
                {brl(data.sales_month)}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-muted-foreground">Pedidos</dt>
              <dd className="font-num" data-testid="month-orders">
                {data.orders_month ?? 0}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-muted-foreground">Produções concluídas</dt>
              <dd className="font-num" data-testid="month-productions">
                {data.productions_finished_month ?? 0}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-muted-foreground">Filamento consumido</dt>
              <dd className="font-num" data-testid="month-consumed">
                {gramas(data.consumed_month_g ?? 0)}
              </dd>
            </div>
          </dl>
          <SectionLink to="/financeiro" testid="go-to-finance-link">
            Ver financeiro
          </SectionLink>
        </Surface>
      </div>
    </div>
  );
}
